import { useQuery } from '@tanstack/react-query';
import { Button, Form, Input, Popconfirm, Select, Spin } from 'antd';
import userApiCall from 'apps/investment/apiCalls/user/list';
import { useEffect, useRef, useState } from 'react';

// Mirrors the backend's validScreenNames (pkg/notification/inapp.go) - keep in sync.
const SCREEN_NAMES = [
  'HomeScreen',
  'FundOrderDetailScreen',
  'OrderListScreen',
  'UserProgramOrderListScreen',
  'UserTotalAssetsStockDetailScreen',
  'StockOrderDetailScreen',
  'UserProgramDetailScreen',
  'EmployeeProgramOrderDetailScreen',
  'RequestOpenAccountsScreen',
  'RequestOpenNHSVAccountScreen',
  'SurveyDetailScreen',
  'IntroBottomSheet',
  'ProductScreen',
  'ProductSearchScreen',
  'FundDetailScreen',
  'CourseDetailScreen',
  'StockDetailScreen',
  'ETFDetailScreen',
  'ManageAccountsScreen',
  'SigningContractScreen',
  'ETFsScreen',
  'CommunityListScreen',
  'CommunityDetailScreen',
  'SubscriptionDetailScreen',
  'OperatingCashScreen',
  'AdvisoryListScreen',
  'ETFHomeScreen',
  'FundHomeScreen',
  'EduHomeBottomTab',
  'UserProgramNewThemeScreen',
  'EduHomeScreen',
  'UserAssetsScreen',
  'PortfolioTemplatesScreen',
  'PortfolioTemplatesSalesScreen',
  'FundTradingRequestScreen',
  'FundTradingEsignRequestScreen',
  'SubscriptionDetailSettingBottomTab',
  'StockHomeScreen',
  'WatchlistScreen',
  'PremiumSubscribeScreen',
];

const screenNameOptions = SCREEN_NAMES.map(name => ({
  value: name,
  label: name.replace(/([a-z])([A-Z])/g, '$1 $2'),
}));

const SendNotificationForm = ({ form, submiting, onFinish, name }: any) => {
  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [fetchOnFocus, setFetchOnFocus] = useState(false);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => setDebouncedUserSearch(userSearch),
      300
    );
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [userSearch]);

  const { data: userResponse, isLoading: isLoadingUsers } = useQuery({
    queryKey: [userApiCall.queryKey, { search: debouncedUserSearch }],
    queryFn: () => userApiCall.queryFn({ search: debouncedUserSearch }),
    enabled: debouncedUserSearch.length > 0 || fetchOnFocus,
  });

  const userOptions = (userResponse?.data || []).map((item: any) => ({
    value: item.id,
    label: `${item.email || item.phone || item.id}${
      item.name ? ` - ${item.name}` : ''
    }`,
  }));

  const screenName = Form.useWatch('screen_name', form);

  // labelInValue so a selected tag keeps its own label once picked, instead
  // of depending on it still being present in the current search results
  // (mode="multiple" needs this - single-select GrantUserModal doesn't).
  const handleFinish = (values: any) => {
    onFinish({
      ...values,
      user_ids: (values.user_ids || []).map((item: any) => item.value),
    });
  };

  return (
    <Form form={form} layout="vertical" name={name || 'form'} onFinish={handleFinish} scrollToFirstError>
      <Form.Item
        name="user_ids"
        label="Recipients"
        rules={[{ required: true, message: 'Please select at least one user' }]}
        extra="Up to 200 recipients per send"
      >
        <Select
          mode="multiple"
          labelInValue
          maxCount={200}
          showSearch
          loading={isLoadingUsers}
          options={userOptions}
          optionFilterProp="label"
          filterOption={false}
          placeholder="Search by email/phone/name"
          onSearch={val => setUserSearch(val)}
          onFocus={() => setFetchOnFocus(true)}
          notFoundContent={isLoadingUsers ? <Spin size="small" /> : null}
        />
      </Form.Item>

      <Form.Item
        name="title"
        label="Title"
        rules={[{ required: true, message: 'Please enter a title' }]}
      >
        <Input maxLength={100} showCount />
      </Form.Item>

      <Form.Item
        name="body"
        label="Body"
        rules={[{ required: true, message: 'Please enter a message' }]}
      >
        <Input.TextArea rows={3} maxLength={500} showCount />
      </Form.Item>

      <Form.Item
        name="screen_name"
        label="Deep link screen (optional)"
        extra="Where tapping the notification navigates to in the app"
      >
        <Select options={screenNameOptions} allowClear showSearch placeholder="None" />
      </Form.Item>

      {screenName && (
        <Form.Item
          name="id"
          label="Entity ID"
          extra="e.g. the fund/order/survey id the screen above should open"
        >
          <Input />
        </Form.Item>
      )}

      <Form.Item>
        <Popconfirm
          title="Send this notification now?"
          description="Pushes immediately to every selected recipient - cannot be undone."
          onConfirm={() => form.submit()}
          okText="Send"
        >
          <Button type="primary" loading={submiting}>
            Send
          </Button>
        </Popconfirm>
      </Form.Item>
    </Form>
  );
};

export default SendNotificationForm;
